import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useTranslation } from "react-i18next";

const CreateNewPassword = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  const qs = new URLSearchParams(location.search);
  const token = qs.get("token") || "";
  const mode = (qs.get("mode") || qs.get("src") || "").toLowerCase(); // "app" ou "" (web)
  const authMotionClass =
    location.state?.authDirection === "toReset"
      ? "auth-slide-from-right"
      : location.state?.authDirection === "toLogin"
      ? "auth-slide-from-left"
      : "auth-slide-soft";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);

  // ✅ même style de message que HelpPage
  // type: "error" | "success" | "info" | ""
  const [msg, setMsg] = useState({ type: "", text: "" });

  const VERIFY_URL = mode === "app" ? "/api/verify_forget" : "/api/password/verify";
  const RESET_URL = mode === "app" ? "/api/change-password" : "/api/password/reset";

  const validatePassword = (pwd) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(pwd);

  // ✅ même logique: message_key -> t(key)
  const translateBackend = (data) => {
    if (!data) return t("msg_server_or_network");
    if (data.message_key) return t(data.message_key, data.message_params || {});
    if (data.error_key) return t(data.error_key);
    if (data.error) return t(String(data.error));
    return t("msg_unknown_error");
  };

  const msgClass =
    msg.type === "error"
      ? "message message--error"
      : msg.type === "success"
      ? "message message--success"
      : msg.type === "info"
      ? "message message--info"
      : "message";

  // ✅ Vérification token
  useEffect(() => {
    if (!token) {
      setMsg({ type: "error", text: t("reset_missing_link_long") });
      setVerifying(false);
      // redirection après petit délai (optionnel)
      setTimeout(() => navigate("/forgot-password", { replace: true }), 1200);
      return;
    }

    (async () => {
      setMsg({ type: "", text: "" });
      try {
        const res = await axios.post(VERIFY_URL, { token });

        const ok = res.status === 200 && (res.data?.ok || res.data?.status === "success");
        if (ok) {
          setVerifying(false);
          setMsg({ type: "", text: "" }); // on nettoie
        } else {
          setVerifying(false);
          setMsg({ type: "error", text: t("reset_err_invalid_or_expired") });
          setTimeout(() => navigate("/forgot-password", { replace: true }), 1200);
        }
      } catch (e) {
        setVerifying(false);
        setMsg({ type: "error", text: translateBackend(e?.response?.data) });
        setTimeout(() => navigate("/forgot-password", { replace: true }), 1200);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, VERIFY_URL, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (!password || !confirmPassword) {
      setMsg({ type: "error", text: t("reset_fill_both_fields") });
      return;
    }

    if (!validatePassword(password)) {
      setMsg({ type: "error", text: t("reset_pwd_rules") });
      return;
    }

    if (password !== confirmPassword) {
      setMsg({ type: "error", text: t("reset_err_password_mismatch") });
      return;
    }

    if (!token) {
      setMsg({ type: "error", text: t("reset_missing_link") });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(RESET_URL, {
        token,
        new_password: password,
        confirm_password: confirmPassword,
      });

      const ok =
        res.status === 200 &&
        (res.data?.status === "success" || res.data?.ok || res.data?.success);

      if (ok) {
        if (mode === "app") {
          setMsg({ type: "success", text: translateBackend(res.data) || t("reset_success") });
          setTimeout(() => navigate("/password-success", { replace: true }), 800);
        } else {
          setMsg({ type: "success", text: translateBackend(res.data) });
          setTimeout(() => navigate("/login", { replace: true }), 1500);
        }
      } else {
        // si backend renvoie un message_key même en 200 mais ok=false
        setMsg({ type: "error", text: translateBackend(res.data) });
      }
    } catch (err) {
      setMsg({ type: "error", text: translateBackend(err?.response?.data) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`auth-shell ${authMotionClass}`}>
      <section className="auth-visual-panel auth-switch-panel">
        <div className="auth-brand-block">
          <span className="auth-brand-mark">A</span>
          <p>{t("assist_by_scan", "ASSIST BY SCAN")}</p>
        </div>

        <div className="auth-orbit" aria-hidden="true">
          <div className="auth-orbit-card auth-orbit-card-main">
            <span className="auth-chip">RESET</span>
            <strong>{t("reset_title", "Créer un nouveau mot de passe")}</strong>
            <small>
              {t(
                "forgot_intro",
                "Entrez votre email et recevez un lien sécurisé pour créer un nouveau mot de passe."
              )}
            </small>
          </div>
          <div className="auth-orbit-card auth-orbit-card-small auth-orbit-card-top">
            <strong>LINK</strong>
            <small>{t("forgot_secure_link", "Lien sécurisé")}</small>
          </div>
          <div className="auth-orbit-card auth-orbit-card-small auth-orbit-card-bottom">
            <strong>24/7</strong>
            <small>{t("auth_proof_access", "Accès sécurisé")}</small>
          </div>
        </div>

        <div className="auth-visual-copy">
          <p className="auth-eyebrow">{t("assist_by_scan", "ASSIST BY SCAN")}</p>
          <h1>
            {t(
              "forgot_story_title",
              "Récupérez votre accès sans interrompre vos interventions."
            )}
          </h1>
        </div>
      </section>

      <section className="auth-workspace-panel auth-switch-card">
        <div className="auth-workspace-card">
          <div className="auth-form-head">
            <span>{t("assist_by_scan", "ASSIST BY SCAN")}</span>
            <strong>{t("reset_title", "Créer un nouveau mot de passe")}</strong>
          </div>

          <h2 className="title mb-3">{t("reset_title")}</h2>

          {msg.text && <p className={msgClass}>{msg.text}</p>}

          {verifying ? (
            <p className="message message--info">{t("reset_verifying")}</p>
          ) : (
            <form onSubmit={handleSubmit} className="form">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("reset_new_password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input input--with-eye"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="eye-btn"
                  aria-label={
                    showPassword
                      ? t("hide_password", "Hide password")
                      : t("show_password", "Show password")
                  }
                  disabled={loading}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t("reset_confirm_password")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input input--with-eye"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="eye-btn"
                  aria-label={
                    showConfirmPassword
                      ? t("hide_password", "Hide password")
                      : t("show_password", "Show password")
                  }
                  disabled={loading}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              <button type="submit" disabled={loading} className="btn btn-lg">
                {loading ? t("reset_saving") : t("save")}
              </button>
            </form>
          )}

          <p className="auth-bottom-link">
            {t("reset_remember")}{" "}
            <Link to="/login" state={{ authDirection: "toLogin" }}>
              {t("nav_login")}
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default CreateNewPassword;
